// ════════════════════════════════════════════════════════════════════════════
// LpaPill — filter chip toggle (aria-pressed + keyboard activation)
// ════════════════════════════════════════════════════════════════════════════

import React from "react";

export interface LpaPillProps {
  /** Texto do pill */
  children: React.ReactNode;
  /** Estado ativo (toggle) */
  active?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Ícone à esquerda */
  icon?: React.ReactNode;
  /** Botão de close (renderiza × à direita) */
  onRemove?: () => void;
  /** Variante visual */
  variant?: "neutral" | "accent" | "success" | "warning" | "danger";
  size?: "sm" | "md";
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  style?: React.CSSProperties;
}

function variantBg(variant: NonNullable<LpaPillProps["variant"]>, active: boolean) {
  if (!active) {
    return {
      background: "var(--lpa-surface)",
      color: "var(--lpa-muted)",
      border: "1px solid var(--lpa-border)",
    };
  }
  switch (variant) {
    case "accent":
      return {
        background: "var(--lpa-accent-soft)",
        color: "var(--lpa-accent)",
        border: "1px solid var(--lpa-accent)",
      };
    case "success":
      return {
        background: "var(--lpa-success-bg)",
        color: "var(--lpa-success)",
        border: "1px solid var(--lpa-success)",
      };
    case "warning":
      return {
        background: "var(--lpa-warning-bg)",
        color: "var(--lpa-warning)",
        border: "1px solid var(--lpa-warning)",
      };
    case "danger":
      return {
        background: "var(--lpa-danger-bg)",
        color: "var(--lpa-danger)",
        border: "1px solid var(--lpa-danger)",
      };
    default:
      return {
        background: "var(--lpa-card)",
        color: "var(--lpa-text)",
        border: "1px solid var(--lpa-border-strong)",
      };
  }
}

export default function LpaPill({
  children,
  active = false,
  onClick,
  icon,
  onRemove,
  variant = "accent",
  size = "sm",
  disabled = false,
  ariaLabel,
  className,
  style,
}: LpaPillProps): React.ReactElement {
  const isInteractive = Boolean(onClick) && !disabled;

  return (
    <span
      role={isInteractive ? "button" : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      aria-pressed={isInteractive ? active : undefined}
      aria-label={ariaLabel}
      onClick={isInteractive ? onClick : undefined}
      onKeyDown={
        isInteractive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      className={className}
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        padding: size === "sm" ? "3px 8px" : "5px 12px",
        fontSize: size === "sm" ? "var(--lpa-text-2xs)" : "var(--lpa-text-sm)",
        fontWeight: 600,
        fontFamily: "var(--lpa-font-mono)",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        borderRadius: "var(--lpa-radius-pill)",
        cursor: isInteractive ? "pointer" : disabled ? "not-allowed" : undefined,
        opacity: disabled ? 0.5 : 1,
        transition: "all var(--lpa-tr-fast)",
        userSelect: "none",
        whiteSpace: "nowrap",
        ...variantBg(variant, active),
        ...style,
      }}
    >
      {icon && (
        <span aria-hidden="true" style={{ display: "inline-flex" }}>
          {icon}
        </span>
      )}
      {children}
      {onRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Remover filtro"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            width: 14,
            height: 14,
            marginLeft: 2,
            padding: 0,
            border: "none",
            background: "transparent",
            color: "inherit",
            cursor: "pointer",
            opacity: 0.7,
            fontSize: 11,
            lineHeight: 1,
          }}
        >
          ×
        </button>
      )}
    </span>
  );
}
